// Phase 7 — Track B T-B4. Payments form validation.
//
// Mirrors the backend's `createPaymentBodySchema` / `updatePaymentBodySchema`
// in `src/modules/payments/subsystems/manual/schemas.ts`. The web SPA uses
// these as the resolver for react-hook-form so the round-trip shape is
// identical and unknown fields fail fast in devtools.
//
// Backend rules carried forward:
//   - `event_id` is caller-supplied (idempotency key); 1..128 chars.
//   - `amount_cents` is an integer (server stores integer cents).
//   - `currency` is uppercase 3-letter ISO 4217.
//   - `merchant` is 1..200 chars; `description` ≤ 2000.
//   - `due_on` / `paid_on` are ISO `YYYY-MM-DD` (TEXT in SQLite).
//   - `recurring_flag` is a boolean.
//   - update body must contain at least one field (no empty PATCH).
//
// Both schemas use `.strict()` per CLAUDE.md §6 #6.

import { z } from 'zod';

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be ISO date YYYY-MM-DD');

const currencySchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'currency must be a 3-letter ISO 4217 code');

const eventIdSchema = z.string().min(1).max(128);

export const createPaymentBodySchema = z
  .object({
    event_id: eventIdSchema,
    amount_cents: z.number().int(),
    currency: currencySchema,
    merchant: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    due_on: isoDateSchema.optional(),
    paid_on: isoDateSchema.optional(),
    recurring_flag: z.boolean(),
  })
  .strict();

export type CreatePaymentBody = z.infer<typeof createPaymentBodySchema>;

export const updatePaymentBodySchema = z
  .object({
    amount_cents: z.number().int().optional(),
    merchant: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    due_on: isoDateSchema.nullable().optional(),
    paid_on: isoDateSchema.nullable().optional(),
    recurring_flag: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'patch must contain at least one field',
  });

export type UpdatePaymentBody = z.infer<typeof updatePaymentBodySchema>;
